import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApiQuery } from "../../../infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../infrastructure/utilities/dateUtility";
import { IUserWorkoutListItemViewModel } from "../models/workoutPlanViewModel";
import styles from "../WorkoutPlanDialog.module.scss";

const TEMPLATE_FALLBACK_NAME = "Тренировка";
const DROPDOWN_GAP = 6;
const VIEWPORT_MARGIN = 8;
const PREFERRED_MAX_HEIGHT = 280;

interface IWorkoutPlanTemplateLoaderProps {
  /** Id of the user the current workout belongs to. Without it we can't list previous workouts. */
  userId?: number;
  /** Current plan id, excluded so the in-progress workout never appears as a template for itself. */
  excludePlanId?: number;
  visible: boolean;
  onLoad: (templateId: number) => Promise<void> | void;
}

interface IDropdownPosition {
  top: number | "auto";
  bottom: number | "auto";
  left: number;
  width: number;
  maxHeight: number;
}

/**
 * Empty-state-only loader that lets the admin start a workout from a previous one
 * instead of building it exercise by exercise. Mirrors the picker's portal-based
 * dropdown so the menu floats above the dialog without clipping.
 */
export const WorkoutPlanTemplateLoader = ({
  userId,
  excludePlanId,
  visible,
  onLoad,
}: IWorkoutPlanTemplateLoaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<IDropdownPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: workouts, isLoading: isFetching } = useApiQuery<
    IUserWorkoutListItemViewModel[]
  >({
    queryKey: [endpointsMetadata.workoutPlan.getUserWorkouts, userId, excludePlanId],
    endpoint: `${endpointsMetadata.workoutPlan.getUserWorkouts}?userId=${userId}${
      excludePlanId ? `&excludePlanId=${excludePlanId}` : ""
    }`,
    // Pre-fetch the list as soon as the loader is rendered so the menu pops open
    // instantly; the disclaimer still makes the trade-off clear to the admin.
    enabled: visible && !!userId,
    globalIsLoading: false,
  });

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const vv = window.visualViewport;
      const vvOffsetTop = vv?.offsetTop ?? 0;
      const vvOffsetLeft = vv?.offsetLeft ?? 0;
      const vvHeight = vv?.height ?? window.innerHeight;

      const spaceBelow = vvHeight - rect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const placeAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
      const availableSpace = placeAbove ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(120, Math.min(PREFERRED_MAX_HEIGHT, availableSpace));

      setDropdownPosition({
        top: placeAbove ? "auto" : rect.bottom + vvOffsetTop + DROPDOWN_GAP,
        bottom: placeAbove
          ? window.innerHeight - rect.top - vvOffsetTop + DROPDOWN_GAP
          : "auto",
        left: rect.left + vvOffsetLeft,
        width: rect.width,
        maxHeight,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", updatePosition);
    vv?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      vv?.removeEventListener("resize", updatePosition);
      vv?.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen, workouts?.length]);

  const handlePick = useCallback(
    async (workout: IUserWorkoutListItemViewModel) => {
      if (isLoading) return;
      setIsLoading(true);
      setIsOpen(false);
      try {
        await onLoad(workout.id);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, onLoad],
  );

  const hasWorkouts = (workouts?.length ?? 0) > 0;
  const isReady = !isFetching && hasWorkouts;
  const triggerDisabled = isLoading || isFetching || !hasWorkouts;

  let triggerLabel: string;
  if (isLoading) {
    triggerLabel = "Зареждане...";
  } else if (isFetching) {
    triggerLabel = "Зареждане на тренировки...";
  } else if (!hasWorkouts) {
    triggerLabel = "Няма предишни тренировки";
  } else {
    triggerLabel = "Зареди от предишна тренировка";
  }

  return (
    <div className={styles.tpl_loader}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.tpl_loader_trigger}
        onClick={() => isReady && setIsOpen((v) => !v)}
        disabled={triggerDisabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.tpl_loader_trigger_label}>{triggerLabel}</span>
        <span className={styles.tpl_loader_trigger_chev} aria-hidden>
          ▾
        </span>
      </button>
      <div className={styles.tpl_loader_disclaimer}>
        При зареждане се копират упражненията и броят серии. Тежести и
        повторения се вземат от последния запис на потребителя за съответното
        упражнение.
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={dropdownRef}
              className={styles.tpl_loader_dropdown}
              style={{
                top: dropdownPosition.top,
                bottom: dropdownPosition.bottom,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: dropdownPosition.maxHeight,
              }}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.16, ease: "easeOut" }}
              role="listbox"
            >
              {workouts?.map((workout) => {
                const displayName =
                  workout.workoutName?.trim() || TEMPLATE_FALLBACK_NAME;
                return (
                  <button
                    key={workout.id}
                    type="button"
                    className={styles.tpl_loader_item}
                    onClick={() => handlePick(workout)}
                    role="option"
                  >
                    <span className={styles.tpl_loader_item_name}>
                      {displayName}
                      {workout.lineageNumber != null && (
                        <span className={styles.tpl_loader_item_badge}>
                          #{workout.lineageNumber}
                        </span>
                      )}
                    </span>
                    <span className={styles.tpl_loader_item_date}>
                      {dateUtility.formatDateTime(workout.date)}
                    </span>
                  </button>
                );
              })}
            </motion.div>
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
};
